import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Target, Zap, TrendingUp, CheckCircle, Phone, Mail, MessageCircle, Copy, Play, Edit3, Plus, BarChart3, Lightbulb } from 'lucide-react';
import { generateClosingScript, generateSalesClosingStrategy } from '../../../services/openai';

const ClosingStrategies = ({ leads = [], campaigns = [], onStrategyApply }) => {
  const [selectedStrategy, setSelectedStrategy] = useState('urgency');
  const [customStrategies, setCustomStrategies] = useState([]);
  const [generatedScript, setGeneratedScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedLeads, setSelectedLeads] = useState([]);

  const strategyTemplates = [
    {
      id: 'urgency',
      name: 'Urgency Close',
      description: 'Create time-sensitive pressure to encourage immediate decision',
      effectiveness: 85,
      bestFor: ['Hot leads', 'Price-sensitive prospects', 'Quarterly goals'],
      techniques: [
        'Limited time offers',
        'Deadline-driven pricing',
        'Inventory scarcity',
        'Seasonal promotions'
      ],
      scriptTemplate: `Based on what we've discussed today, I can see this solution would save you ${'{'}savings{'}'} annually. I have one spot left in our implementation schedule for ${'{'}month{'}'}, and I can lock in this pricing until ${'{'}deadline{'}'}. Would you like to secure your spot today?`,
      successRate: 68
    },
    {
      id: 'assumptive',name: 'Assumptive Close',description: 'Assume the prospect will buy and guide them through next steps',
      effectiveness: 78,
      bestFor: ['Qualified leads', 'Decision makers', 'Engaged prospects'],
      techniques: [
        'Implementation timeline discussion','Team introduction planning','Onboarding preparation','Success metrics setup'
      ],
      scriptTemplate: `Great! Now that we've covered everything, let me walk you through our implementation process. We typically start with ${'{'}process{'}'} in week one. When would be the best time for our implementation team to connect with yours?`,
      successRate: 72
    },
    {
      id: 'question',
      name: 'Question Close',
      description: 'Use strategic questions to guide prospect to self-close',
      effectiveness: 82,
      bestFor: ['Analytical buyers', 'Committee decisions', 'Complex sales'],
      techniques: [
        'Discovery questions',
        'Confirmation questions',
        'Alternative choice questions',
        'Consequence questions'
      ],
      scriptTemplate: `If you could solve ${'{'}pain_point{'}'} and achieve ${'{'}desired_outcome{'}'} within ${'{'}timeframe{'}'}, what would that mean for your business? And what's preventing you from moving forward today?`,
      successRate: 75
    },
    {
      id: 'summary',name: 'Summary Close',description: 'Summarize key benefits and value proposition before asking for commitment',
      effectiveness: 76,
      bestFor: ['Complex solutions', 'Multiple stakeholders', 'Long sales cycles'],
      techniques: [
        'Benefit summary','ROI recap','Risk mitigation review','Success story parallels'
      ],
      scriptTemplate: `Let me summarize what we've covered: Our solution will ${'{'}benefit_1{'}'}, ${'{'}benefit_2{'}'}, and ${'{'}benefit_3{'}'}. With a total ROI of ${'{'}roi{'}'}, you'll see payback in ${'{'}payback_period{'}'}. Based on everything we've discussed, does this make sense for your business?`,
      successRate: 71
    },
    {
      id: 'alternative',
      name: 'Alternative Choice Close',
      description: 'Present two options, both leading to a sale',
      effectiveness: 79,
      bestFor: ['Indecisive buyers', 'Multiple product tiers', 'Custom solutions'],
      techniques: [
        'Package comparisons',
        'Implementation options',
        'Timeline alternatives',
        'Investment levels'
      ],
      scriptTemplate: `I see two great options for you: Option A gives you ${'{'}option_a_benefits{'}'} for ${'{'}price_a{'}'}, while Option B includes ${'{'}option_b_benefits{'}'} for ${'{'}price_b{'}'}. Which one feels like the better fit for your current situation?`,
      successRate: 74
    },
    {
      id: 'objection',
      name: 'Objection Handling Close',
      description: 'Address concerns while moving toward commitment',
      effectiveness: 73,
      bestFor: ['Skeptical prospects', 'Price objections', 'Competitive situations'],
      techniques: [
        'Acknowledge and reframe',
        'Social proof',
        'Risk reversal',
        'Guarantee positioning'
      ],
      scriptTemplate: `I understand your concern about ${'{'}objection{'}'}. Many of our clients had the same worry initially. What we found is ${'{'}solution{'}'}, which is why we offer ${'{'}guarantee{'}'}. Given that assurance, what other concerns do you have?`,
      successRate: 69
    }
  ];

  const generateAIStrategy = async (leadIds) => {
    if (leadIds?.length === 0) return;
    
    setLoading(true);
    try {
      const selectedLeadData = leads?.filter(lead => leadIds?.includes(lead?.id));
      const primaryLead = selectedLeadData?.[0];
      
      const leadData = {
        name: primaryLead?.name,
        company: primaryLead?.company,
        score: primaryLead?.score,
        status: primaryLead?.status,
        potentialValue: primaryLead?.potentialValue,
        tags: primaryLead?.tags,
        engagementLevel: primaryLead?.engagementLevel
      };

      const salesContext = {
        leadCount: selectedLeadData?.length,
        avgValue: selectedLeadData?.reduce((sum, lead) => sum + lead?.potentialValue, 0) / selectedLeadData?.length,
        timeline: '30 days',
        competition: 'moderate'
      };

      const strategy = await generateSalesClosingStrategy(leadData, salesContext);
      
      // Generate script for primary technique
      if (strategy?.recommended_techniques?.length > 0) {
        const primaryTechnique = strategy?.recommended_techniques?.[0];
        const leadProfile = {
          name: primaryLead?.name,
          company: primaryLead?.company,
          painPoints: ['efficiency', 'cost', 'scalability'],
          interests: primaryLead?.tags,
          industry: 'technology'
        };

        const script = await generateClosingScript(leadProfile, primaryTechnique?.technique, {
          meetingType: 'closing_call',
          urgency: primaryLead?.status === 'hot' ? 'high' : 'medium'
        });

        setGeneratedScript({
          strategy,
          script,
          appliedTo: selectedLeadData?.length,
          leads: selectedLeadData
        });
      }
    } catch (error) {
      console.error('Error generating AI strategy:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSelection = (leadId) => {
    setSelectedLeads(prev => {
      if (prev?.includes(leadId)) {
        return prev?.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const selectedStrategyData = strategyTemplates?.find(s => s?.id === selectedStrategy);

  return (
    <div className="space-y-6">
      {/* Strategy Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        {[
          { id: 'templates', label: 'Strategy Templates', icon: MessageSquare },
          { id: 'ai-generator', label: 'AI Generator', icon: Zap },
          { id: 'active-campaigns', label: 'Active Campaigns', icon: BarChart3 }
        ]?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab?.id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab?.label}
          </button>
        ))}
      </div>
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Strategy Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Target className="h-5 w-5 mr-2 text-blue-600" />
              Closing Strategy Templates
            </h3>
            
            <div className="space-y-3">
              {strategyTemplates?.map((strategy) => (
                <motion.div
                  key={strategy?.id}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setSelectedStrategy(strategy?.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedStrategy === strategy?.id
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{strategy?.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{strategy?.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                        <span className="text-sm font-medium text-green-600">
                          {strategy?.effectiveness}%
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">effectiveness</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 mr-1 text-blue-500" />
                      {strategy?.successRate}% success rate
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {strategy?.bestFor?.slice(0, 2)?.map((item, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Strategy Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900">{selectedStrategyData?.name}</h4>
              <p className="text-gray-600 mt-1">{selectedStrategyData?.description}</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Effectiveness Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-600">Effectiveness</span>
                    <span className="text-2xl font-bold text-green-900">
                      {selectedStrategyData?.effectiveness}%
                    </span>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-600">Success Rate</span>
                    <span className="text-2xl font-bold text-blue-900">
                      {selectedStrategyData?.successRate}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Best For */}
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Best For:</h5>
                <div className="flex flex-wrap gap-2">
                  {selectedStrategyData?.bestFor?.map((item, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Techniques */}
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Key Techniques:</h5>
                <ul className="space-y-2">
                  {selectedStrategyData?.techniques?.map((technique, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                      {technique}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Script Template */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-gray-900">Script Template:</h5>
                  <button
                    onClick={() => copyToClipboard(selectedStrategyData?.scriptTemplate)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-800 italic leading-relaxed">
                    {selectedStrategyData?.scriptTemplate}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
                  <Play className="h-4 w-4 mr-2" />
                  Apply Strategy
                </button>
                <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center">
                  <Edit3 className="h-4 w-4 mr-2" />
                  Customize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'ai-generator' && (
        <div className="space-y-6">
          {/* Lead Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Lightbulb className="h-5 w-5 mr-2 text-yellow-500" />
                AI-Powered Strategy Generator
              </h3>
              <p className="text-gray-600 mt-1">Select leads and let AI generate personalized closing strategies</p>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {leads?.map((lead) => (
                  <motion.div
                    key={lead?.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleLeadSelection(lead?.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedLeads?.includes(lead?.id)
                        ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{lead?.name}</h4>
                      {selectedLeads?.includes(lead?.id) && (
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{lead?.company}</p>
                    <div className="flex items-center justify-between mt-2 text-sm">
                      <span className="text-gray-600">Score: {lead?.score}</span>
                      <span className="text-green-600 font-medium">
                        ${lead?.potentialValue?.toLocaleString()}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-6 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedLeads?.length} leads selected
                </span>
                <button
                  onClick={() => generateAIStrategy(selectedLeads)}
                  disabled={selectedLeads?.length === 0 || loading}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Generate AI Strategy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Generated Strategy */}
          {generatedScript && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Zap className="h-5 w-5 mr-2 text-purple-600" />
                  AI-Generated Strategy
                </h3>
                <p className="text-gray-600 mt-1">
                  Strategy generated for {generatedScript?.appliedTo} lead(s)
                </p>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Strategy Overview */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-purple-600">Lead Score</span>
                      <span className="text-2xl font-bold text-purple-900">
                        {generatedScript?.strategy?.lead_score}
                      </span>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-600">Close Probability</span>
                      <span className="text-2xl font-bold text-green-900">
                        {generatedScript?.strategy?.closing_probability}
                      </span>
                    </div>
                  </div>
                </div>

                {/* AI Script */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <MessageCircle className="h-5 w-5 mr-2 text-blue-600" />
                    AI-Generated Closing Script
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h5 className="font-medium text-blue-900 mb-2">Opening</h5>
                      <p className="text-blue-800">{generatedScript?.script?.opening}</p>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <h5 className="font-medium text-green-900 mb-2">Main Script</h5>
                      <p className="text-green-800 whitespace-pre-line">{generatedScript?.script?.main_script}</p>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h5 className="font-medium text-purple-900 mb-2">Closing Question</h5>
                      <p className="text-purple-800 font-medium">{generatedScript?.script?.closing_question}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center justify-center">
                    <Play className="h-4 w-4 mr-2" />
                    Launch Campaign
                  </button>
                  <button 
                    onClick={() => copyToClipboard(generatedScript?.script?.main_script)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Script
                  </button>
                  <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center">
                    <Edit3 className="h-4 w-4 mr-2" />
                    Refine
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}
      {activeTab === 'active-campaigns' && (
        <div className="space-y-6">
          {/* Active Campaigns */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Active Closing Campaigns</h3>
                <p className="text-gray-600 mt-1">Monitor and manage your ongoing closing strategies</p>
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Leads
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Close Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns?.map((campaign) => (
                    <tr key={campaign?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{campaign?.name}</div>
                          <div className="text-sm text-gray-500">Started 3 days ago</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {campaign?.type === 'email' ? (
                            <Mail className="h-4 w-4 mr-2 text-blue-500" />
                          ) : campaign?.type === 'sms' ? (
                            <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
                          ) : (
                            <Phone className="h-4 w-4 mr-2 text-purple-500" />
                          )}
                          <span className="text-sm text-gray-900 capitalize">{campaign?.type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaign?.totalLeads}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900">{campaign?.responseRate}%</span>
                          <TrendingUp className="h-4 w-4 ml-1 text-green-500" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900">{campaign?.closeRate}%</span>
                          <Target className="h-4 w-4 ml-1 text-blue-500" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${campaign?.revenue?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          campaign?.status === 'active' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign?.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClosingStrategies;